// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import styles from "@mcmc/App/Widgets/ProductInfoCard/ProductInfoCard.style";
import LazyImage from "@mcmc/MobiusOverrides/LazyImage";
import LinkButton from "@mcmc/XDL/Components/Common/LinkButton";
import parse from "html-react-parser";
import React, { createRef, useEffect, useState } from "react";

const enum fields {
    imageUrl = "imageUrl",
    imageCaption = "imageCaption",
    copy = "copy",
    buttons = "buttons",
    // button fields
    destination = "destination",
    overriddenTitle = "overriddenTitle",
    variant = "variant",
    openInNewWindow = "openInNewWindow",
}

type ButtonVariant = "primary" | "secondary" | "tertiary";

interface ButtonModel {
    url: string;
    [fields.openInNewWindow]: boolean;
    [fields.overriddenTitle]: string;
    [fields.variant]: ButtonVariant;
    [fields.destination]: LinkFieldValue;
}

interface ButtonFieldModel extends LinkModel {
    fields: ButtonModel;
}

interface ProductInfoCardWidgetProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.imageCaption]: string;
        [fields.copy]: string;
        [fields.buttons]: ButtonFieldModel[];
    };
}

const ProductInfoCardWidget: React.FC<ProductInfoCardWidgetProps> = ({
    fields: { imageCaption, copy, imageUrl, buttons: fieldButtons },
}) => {
    const imageCaptionRef = createRef<HTMLDivElement>();
    const [imageAltText, setImageAltText] = useState<string>("");
    const buttons = useGetLinks(fieldButtons, o => o.fields.destination);

    useEffect(() => {
        setImageAltText(imageCaptionRef.current?.textContent || "");
    }, []);

    return (
        <StyledWrapper {...styles.wrapper}>
            <LazyImage src={imageUrl} {...styles.image} altText={imageAltText} />
            <StyledWrapper {...styles.textContentWrapper}>
                {/* TODO: Use react-lines-ellipsis for heading */}
                <StyledWrapper ref={imageCaptionRef} {...styles.heading}>
                    {parse(imageCaption, parserOptions)}
                </StyledWrapper>
                {/* TODO: Use react-lines-ellipsis for heading */}
                {copy && (
                    <StyledWrapper {...styles.copyWrapper}>
                        <Typography {...styles.copyText}>{copy}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.buttonsWrapper}>
                    {buttons.map(
                        (button, index) =>
                            button?.url && (
                                <LinkButton
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${button.url}${index}`}
                                    id={`learnMore_${imageAltText.replace(/\s+/g, '')}`}
                                    href={button.url}
                                    target={fieldButtons[index].fields[fields.openInNewWindow] ? "_blank" : undefined}
                                    variant={fieldButtons[index].fields[fields.variant]}
                                    {...styles.button}
                                >
                                    {fieldButtons[index].fields[fields.overriddenTitle]}
                                </LinkButton>
                            ),
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.imageUrl,
            fieldType: "Contextual",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.imageCaption,
            fieldType: "Contextual",
            editorTemplate: "RichTextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.copy,
            displayName: "Copy",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Contextual",
            isRequired: false,
        },
        {
            name: fields.buttons,
            editorTemplate: "ListField",
            getDisplay: (button: HasFields, state) => {
                const { type, value } = button.fields.destination;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return link?.title;
                }
                if (type === "Category") {
                    return value; // TODO ISC-10781 make this work
                }
                if (type === "Url") {
                    return value;
                }
                return value;
            },
            validate: (buttons: HasFields[]) => {
                if (!buttons || buttons.length === 0) {
                    return "At least one button must be added.";
                }
                if (buttons.length > 2) {
                    return "At max two buttons can be added.";
                }
                return null;
            },
            defaultValue: [],
            fieldType: "Contextual",
            fieldDefinitions: [
                {
                    name: fields.destination,
                    displayName: "Button Link",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                    validate: (link: LinkFieldValue) => {
                        if (!link || !link.value) {
                            return "A value must be provided.";
                        }
                        return null;
                    },
                },
                {
                    name: fields.overriddenTitle,
                    displayName: "Button Label",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: fields.variant,
                    displayName: "Button Variant",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Primary", value: "primary" },
                        { displayName: "Secondary", value: "secondary" },
                        { displayName: "Tertiary", value: "tertiary" },
                    ],
                    defaultValue: "primary",
                    isRequired: true,
                },
                {
                    name: fields.openInNewWindow,
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ProductInfoCardWidget,
    definition,
};

export default widgetModule;
